import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import FooterMenusApi from 'libs/api/website/footerMenus';
import AuthService from 'services/Auth';

const useFooterMenus = () => {
  const [footerMenus, setFooterMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({});
  const [shoppingCenters, setShoppingCenters] = useState([]);
  const [loadingShoppingCenters, setLoadingShoppingCenters] = useState(false);

  const navigate = useNavigate();
  const paramsRef = useRef({});

  const checkAuth = useCallback(() => {
    const token = AuthService.getToken();
    if (!token) {
      message.error('You are not authorized. Please login.');
      navigate('/login');
      return false;
    }
    return true;
  }, [navigate]);

  const fetchFooterMenus = useCallback(
    async (params = {}) => {
      if (!checkAuth()) return;

      setLoading(true);
      try {
        const response = await FooterMenusApi.list(params);

        if (response.data.success) {
          setFooterMenus(response.data.footer_menus || []);
          setTotal(response.data.pagination?.total_count || 0);
        } else {
          setFooterMenus([]);
          setTotal(0);
        }
      } catch (error) {
        console.error('Error fetching footer menus:', error);
        if (error.response?.status === 401) {
          message.error('You are not authorized to access this page.');
          navigate('/login');
        } else {
          message.error(
            error.response?.data?.message || 'Failed to load footer menus',
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [checkAuth, navigate],
  );

  const fetchFooterMenu = useCallback(
    async (id) => {
      if (!checkAuth()) return null;

      try {
        const response = await FooterMenusApi.show(id);
        if (response.data.success) {
          return response.data.footer_menu;
        }
        return null;
      } catch (error) {
        console.error('Error fetching footer menu:', error);
        if (error.response?.status === 401) {
          message.error('You are not authorized to access this page.');
          navigate('/login');
        } else {
          message.error(
            error.response?.data?.message || 'Failed to load footer menu',
          );
        }
        return null;
      }
    },
    [checkAuth, navigate],
  );

  const createFooterMenu = useCallback(
    async (data) => {
      if (!checkAuth()) return null;

      try {
        const response = await FooterMenusApi.create(data);

        if (response.data.success) {
          return response.data.footer_menu || response.data;
        }

        const error = new Error(
          response.data.message || 'Failed to create footer menu',
        );
        error.response = { data: response.data };
        throw error;
      } catch (error) {
        console.error('Error creating footer menu:', error);
        if (error.response?.status === 401) {
          message.error('You are not authorized to perform this action.');
          navigate('/login');
          return null;
        }
        throw error;
      }
    },
    [checkAuth, navigate],
  );

  const updateFooterMenu = useCallback(
    async (id, data) => {
      if (!checkAuth()) return null;

      try {
        const response = await FooterMenusApi.update(id, data);

        if (response.data.success) {
          return response.data.footer_menu || response.data;
        }

        const error = new Error(
          response.data.message || 'Failed to update footer menu',
        );
        error.response = { data: response.data };
        throw error;
      } catch (error) {
        console.error('Error updating footer menu:', error);
        if (error.response?.status === 401) {
          message.error('You are not authorized to perform this action.');
          navigate('/login');
          return null;
        }
        throw error;
      }
    },
    [checkAuth, navigate],
  );

  const deleteFooterMenu = useCallback(
    async (id) => {
      if (!checkAuth()) return false;

      try {
        const response = await FooterMenusApi.destroy(id);
        if (response.data?.success !== false) {
          message.success('Footer menu deleted successfully');
          return true;
        }
        message.error(
          response.data.message || 'Failed to delete footer menu',
        );
        return false;
      } catch (error) {
        console.error('Error deleting footer menu:', error);
        if (error.response?.status === 401) {
          message.error('You are not authorized to perform this action.');
          navigate('/login');
        } else {
          message.error(
            error.response?.data?.message || 'Failed to delete footer menu',
          );
        }
        return false;
      }
    },
    [checkAuth, navigate],
  );

  const fetchFilters = useCallback(async () => {
    if (!checkAuth()) return;

    setLoadingShoppingCenters(true);
    try {
      const response = await FooterMenusApi.getFilters();
      if (response.data.success) {
        setShoppingCenters(response.data.shopping_centers || []);
      }
    } catch (error) {
      console.error('Error fetching filters:', error);
      message.error('Failed to load filters');
    } finally {
      setLoadingShoppingCenters(false);
    }
  }, [checkAuth]);

  const updateFilters = useCallback((newFilters) => {
    if (Object.keys(newFilters).length === 0) {
      setFilters({});
    } else {
      setFilters((prev) => ({ ...prev, ...newFilters }));
    }
    setCurrentPage(1);
  }, []);

  const handleTableChange = useCallback((pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  }, []);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    const params = {
      page: currentPage,
      per: pageSize,
      ...filters,
    };

    Object.keys(params).forEach((key) => {
      if (
        params[key] === undefined ||
        params[key] === null ||
        params[key] === ''
      ) {
        delete params[key];
      }
    });

    paramsRef.current = params;
    fetchFooterMenus(params);
  }, [currentPage, pageSize, filters, fetchFooterMenus]);

  return {
    footerMenus,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    shoppingCenters,
    loadingShoppingCenters,
    fetchFooterMenus,
    fetchFooterMenu,
    createFooterMenu,
    updateFooterMenu,
    deleteFooterMenu,
    handleTableChange,
    updateFilters,
    setFilters,
  };
};

export default useFooterMenus;
